/**
 * Silhouette / text path builder（happa v1.1）。
 *
 * innerPoly（subdivided + warped）からテキスト経路 `TextPathData` を構築する
 * pure ロジック群。spec §5.4.1 / wireframe v0.10 L634–691 と挙動一致。
 *
 * 主要な 4 関数:
 *
 * - `cornerIndices(N, perEdge)`:
 *   元の正多角形頂点に対応する subdivided ポリゴンの頂点インデックス列を生成。
 * - `rotateHullWithCorners(hull, cornerIdx)`:
 *   最上端頂点から開始するよう hull を回転し、`cornerIdx` も追従させる。
 * - `buildTextPath(polygon, cornerIdx)`:
 *   各辺を線形に細かく刻み、tangent + 累積距離を持つ `SilhouettePoint` 列に展開。
 *   `cornerIdx` で指示された頂点に到達した時点の累積距離を `corners` に記録。
 * - `samplePath(points, target)`:
 *   累積距離 `target` 以上となる最初の点を下限二分探索で返す（補間なし）。
 *
 * 全関数 pure。DOM / Canvas / 状態を参照しない。
 *
 * 設計参照:
 * - spec §5.4.1（経路構築、L471–535）
 * - wireframe `wireframes/v0.10/wireframe.html` L634–691
 */

import type { AppState } from '../state/types';
import { applyBreath } from './breath';
import { subdividePolygon, warpPolygonByCells } from './polygon';
import type { Scene, SilhouettePoint, TextPathData, Vec2 } from './types';

/**
 * 元の正多角形頂点に対応する subdivided ポリゴン内の頂点インデックス列。
 *
 * `subdividePolygon(poly, perEdge)` は各辺を `perEdge` 等分するため、
 * 元の頂点 `i` は subdivided 配列の `i × perEdge` 番目に位置する（`polygon.ts`
 * の `subdividePolygon` が「始点含む・終点含まない」で刻むため、整合する）。
 *
 * 戻り値の長さは `N`。各値は `0..N × perEdge - 1` の範囲に収まる。
 *
 * 設計参照: spec §5.4.1 L478–481。
 *
 * @param N 元の正多角形頂点数（3 以上を想定、0 なら空配列）。
 * @param perEdge 1 辺あたりの分割数（`subdividePolygon` に渡した値と同じ）。
 * @returns 長さ `N` のインデックス配列 `[0, perEdge, 2 × perEdge, ...]`。
 */
export function cornerIndices(N: number, perEdge: number): number[] {
  return Array.from({ length: N }, (_, i) => i * perEdge);
}

/**
 * `rotateHullWithCorners` の戻り値型。
 *
 * `Hull` 型は contract ファイルだが、本関数は **生タプル**（`rotated` +
 * `newCornerIndices`）を返す（spec §5.4.1 L492、wireframe L647 と一致）。
 * `Hull` への詰め替えは呼び出し側の責務。
 */
export interface RotateHullResult {
  /** 最上端頂点を先頭にした hull。長さは入力 `hull` と同じ。 */
  rotated: Vec2[];
  /**
   * 回転後の配列における corner 頂点インデックス列（長さは入力 `cornerIdx`
   * と同じ）。各値は `0..hull.length-1` の範囲。
   */
  newCornerIndices: number[];
}

/**
 * 最上端（最小 Y）頂点を先頭にくるよう hull を巡回シフトする。
 *
 * wireframe L637–648 と spec §5.4.1 L484–493 に準拠した実装。
 *
 * 手順:
 * 1. `hull` を走査し最小 Y 頂点 `topIdx` を特定（タイ時は最初に現れた index）。
 * 2. `rotated = hull.slice(topIdx).concat(hull.slice(0, topIdx))` で巡回シフト。
 * 3. `cornerIdx` の各値を `(i - topIdx + N) % N` で回転後配列の index に変換。
 *
 * Canvas 座標系では Y が下向きに増えるため「最小 Y ＝ 最上端」となる。
 * これによりテキスト経路の始点が常にハルの上側から始まり、読み順が
 * 視覚的に自然になる（spec §5.4.1）。
 *
 * 空入力（`hull.length === 0`）は `rotated = []`、`newCornerIndices = []`
 * を返す。`cornerIdx` の値が `hull.length` 以上の場合も `% N` で処理されるが、
 * そのような入力は上流（`cornerIndices` + `subdividePolygon`）で発生しない
 * 前提。
 *
 * @param hull subdivided + warped なポリゴン頂点列（閉じた多角形）。
 * @param cornerIdx 元の頂点に対応する `hull` 内 index 列（`cornerIndices` 戻り値）。
 * @returns `{ rotated, newCornerIndices }` の生タプル（`Hull` オブジェクトではない）。
 */
export function rotateHullWithCorners(hull: Vec2[], cornerIdx: number[]): RotateHullResult {
  const N = hull.length;
  if (N === 0) return { rotated: [], newCornerIndices: [] };

  let topIdx = 0;
  let minY = Number.POSITIVE_INFINITY;
  for (let i = 0; i < N; i++) {
    const v = hull[i];
    if (!v) continue;
    if (v[1] < minY) {
      minY = v[1];
      topIdx = i;
    }
  }

  const rotated = [...hull.slice(topIdx), ...hull.slice(0, topIdx)];
  const newCornerIndices = cornerIdx.map((i) => (i - topIdx + N) % N);
  return { rotated, newCornerIndices };
}

/**
 * ポリゴンの辺を線形に細かく刻み、テキスト経路 `TextPathData` を構築する。
 *
 * spec §5.4.1 L496–522 / wireframe L650–680 と bit 単位で同等。
 *
 * 各辺について:
 * - 向きベクトル `(dx, dy) = b - a`、長さ `len = hypot(dx, dy)`。
 * - `len < 1e-3` の極短辺はスキップ（累積距離も更新しない。退化入力の安全側）。
 * - `tangent = atan2(dy, dx)`（辺に沿った向き、rad）。
 * - `steps = max(3, ceil(len / 4))` 個のサンプル点を `t = s/steps` で線形配置
 *   （`s = 0..steps-1`、`s === steps` は次辺の始点と重複するため除外）。
 * - 各点に `cumulative + len * t` を記録し、辺処理後 `cumulative += len`。
 *
 * `corners` には `cornerIdx` に含まれる頂点 `i` を処理するタイミングで
 * その時点の `cumulative` を push する。ただし `i === 0` は除外（spec §5.4.1
 * L504 と wireframe L659 が明示的に `i !== 0` を弾く。開始点に corner が
 * 重複すると最終 `corners[0] === 0` となりテキストレイアウト側が誤動作する
 * ため）。
 *
 * `totalLength` は全辺長の総和（skip された辺を除く）。本関数はサンプル点の
 * 末尾に `cumulative` を含めないため、`samplePath` の target が `totalLength`
 * 近傍で最後のサンプル点を指すよう設計されている。
 *
 * @param polygon 閉じた多角形（subdivided + warped + offset 済み）。
 * @param cornerIdx 元の頂点に対応する `polygon` 内 index 列
 *                  （`rotateHullWithCorners` の `newCornerIndices`）。
 * @returns `{ points, totalLength, corners }`。退化入力では空 path を返す。
 */
export function buildTextPath(polygon: Vec2[], cornerIdx: number[]): TextPathData {
  const N = polygon.length;
  if (N < 2) return { points: [], totalLength: 0, corners: [] };

  const points: SilhouettePoint[] = [];
  const corners: number[] = [];
  const cornerSet = new Set(cornerIdx);
  let cumulative = 0;

  for (let i = 0; i < N; i++) {
    // 元頂点に到達した瞬間の累積距離を記録。i === 0 は除外（spec §5.4.1 L504）。
    if (cornerSet.has(i) && i !== 0) corners.push(cumulative);

    const a = polygon[i];
    const b = polygon[(i + 1) % N];
    if (!a || !b) continue;

    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.sqrt(dx * dx + dy * dy);
    // 極短辺（1e-3 px 未満）はサンプル不能なので累積も更新せずスキップ。
    if (len < 1e-3) continue;

    const tangent = Math.atan2(dy, dx);
    // `len / 4` を丸め上げることで辺間の平均サンプル間隔を概ね 4px に保つ。
    // 下限 3 は短辺でも tangent 推定と二分探索がまともに機能するための保険。
    const steps = Math.max(3, Math.ceil(len / 4));
    for (let s = 0; s < steps; s++) {
      const t = s / steps;
      points.push({
        x: a[0] + dx * t,
        y: a[1] + dy * t,
        tangent,
        cumulative: cumulative + len * t,
      });
    }
    cumulative += len;
  }

  return { points, totalLength: cumulative, corners };
}

/**
 * 累積距離 `target` 以上となる最初のサンプル点を返す（下限二分探索）。
 *
 * spec §5.4.1 L525–533 / wireframe L682–691 と挙動一致。線形補間は **行わない**
 * （テキスト描画側は cumulative が大きめに離散しても字間圧縮が効くため、
 * 補間精度より決定論性とコスト優先）。
 *
 * 空入力では `null`。`target` が負や `totalLength` 超過でも範囲外判定はせず、
 * それぞれ先頭 or 末尾の点を返す（`cumulative` が単調増加 + 末尾点 ≤
 * totalLength の前提）。呼び出し側で mod 演算後に渡すのが想定パス
 * （`samplePath(points, pos % totalLength)`）。
 *
 * 計算量 `O(log n)`。`points` は `buildTextPath` により cumulative 昇順を
 * 保証されている。
 *
 * @param points `TextPathData.points`（累積距離昇順）。
 * @param target 取得したい累積距離（px）。
 * @returns 条件を満たす最初の点。空入力なら `null`。
 */
export function samplePath(points: SilhouettePoint[], target: number): SilhouettePoint | null {
  const n = points.length;
  if (n === 0) return null;

  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const p = points[mid];
    // `points[mid]` は cumulative 昇順で必ず存在する（noUncheckedIndexedAccess
    // 対策の null チェック）。欠損時は右半分へ寄せる安全側フォールバック。
    if (!p || p.cumulative < target) lo = mid + 1;
    else hi = mid;
  }
  return points[lo] ?? null;
}

// ────────────────────────────────────────────────────────────────────────────
// roundSharpCorners — v1.1 Phase C.1 Step 1 追加
// ────────────────────────────────────────────────────────────────────────────

/**
 * polygon の符号付き面積（Shoelace formula、正＝CCW、負＝CW）。
 *
 * `roundSharpCorners` 内で巻き向き判定にのみ使用する private helper。
 * 内角は本来「polygon 内側へ向かう側」で測るが、頂点列のローカル cross 符号
 * だけでは外角と区別できない。そこで polygon 全体の signed area の符号と
 * 各頂点 cross の符号を比較し、巻き向きと一致する側を内側として採用する。
 */
function signedArea(path: Vec2[]): number {
  const n = path.length;
  let s = 0;
  for (let i = 0; i < n; i++) {
    const a = path[i];
    const b = path[(i + 1) % n];
    if (!a || !b) continue;
    s += a[0] * b[1] - b[0] * a[1];
  }
  return s * 0.5;
}

/**
 * 1 頂点を入力として、それを丸めた円弧サンプル列を返す（鋭角でなければ null）。
 *
 * 「入力ベースの prev / v / next」で独立処理する設計。連続する鋭角でも
 * 補間後の頂点を参照しないため、結果が頂点順序に依存しない。
 *
 * - `interior` は polygon 巻き向きを考慮して `[0, π]` に正規化された内角。
 * - 接線長 `t = rEff / tan(interior/2)` を `0.4 * min(|e1|,|e2|)` で clamp し、
 *   隣接辺長より大きい円弧を作らないようにする（接点が辺をはみ出す事故を防ぐ）。
 * - 補間点数は `clamp(round((π - interior) / (π/8)), 4, 8)`。
 *   外角 (π - interior) を「π/8 ≒ 22.5°」刻みで分割する感覚値。
 *
 * 退化ケース:
 * - 辺長が 1e-6 未満 → null（呼び出し側で原値保持）
 * - sin(interior/2) が 0 近傍（≒直線、内角 π） → null
 */
function roundOneCorner(
  prev: Vec2,
  v: Vec2,
  next: Vec2,
  ccw: boolean,
  angleThresholdDeg: number,
  roundRadius: number,
): Vec2[] | null {
  const e1x = v[0] - prev[0];
  const e1y = v[1] - prev[1];
  const e2x = next[0] - v[0];
  const e2y = next[1] - v[1];
  const len1 = Math.hypot(e1x, e1y);
  const len2 = Math.hypot(e2x, e2y);
  if (len1 < 1e-6 || len2 < 1e-6) return null;

  // signed turn angle（CCW polygon の場合: 左折＝正、右折＝負）。
  // cross は e1 × e2、dot は e1・e2（v に入る向き e1 と v から出る向き e2）。
  const cross = e1x * e2y - e1y * e2x;
  const dot = e1x * e2x + e1y * e2y;
  const turn = Math.atan2(cross, dot); // [-π, π]

  // 巻き向きと一致する側が内側。CCW なら左折(turn>0)時 interior = π - turn。
  // 反対側（外側）に turn している場合 interior = π + |turn|（鈍角扱い → 丸めない）。
  const sameSide = ccw ? turn > 0 : turn < 0;
  const interior = sameSide ? Math.PI - Math.abs(turn) : Math.PI + Math.abs(turn);

  // 内角が threshold 以上、または直線近傍ならスキップ。
  const thresholdRad = (angleThresholdDeg * Math.PI) / 180;
  if (interior >= thresholdRad) return null;
  const halfInt = interior * 0.5;
  const sinHalf = Math.sin(halfInt);
  const tanHalf = Math.tan(halfInt);
  if (sinHalf < 1e-6 || tanHalf < 1e-6) return null;

  // radius を接線長基準で clamp。接線長 = r / tan(interior/2)。
  // この接線長が 0.4 * min(len1, len2) を超えないよう逆算（r ≤ 0.4*minLen*tanHalf）。
  const maxByEdge = 0.4 * Math.min(len1, len2);
  const rByTangent = maxByEdge * tanHalf;
  const rEff = Math.min(roundRadius, maxByEdge, rByTangent);
  if (rEff < 1e-6) return null;

  return interpolateArc(v, e1x, e1y, e2x, e2y, len1, len2, rEff, halfInt, sinHalf, tanHalf);
}

/**
 * 円弧中心と両接点を slerp で等分し、補間サンプル列を返す。
 *
 * 接点 P1 / P2 は v から各辺の **内側** へ接線長 `t` だけ戻った位置。
 * 円弧中心 C は二等分線方向（v から内側へ）に距離 `rEff / sinHalf` 進んだ位置。
 * C → P1, C → P2 は半径 rEff のベクトルで、両者を slerp で `segs` 等分する。
 *
 * `roundOneCorner` から呼ぶ private helper。40 行制限のため分離した。
 */
function interpolateArc(
  v: Vec2,
  e1x: number,
  e1y: number,
  e2x: number,
  e2y: number,
  len1: number,
  len2: number,
  rEff: number,
  halfInt: number,
  sinHalf: number,
  tanHalf: number,
): Vec2[] {
  const tangentLen = rEff / tanHalf;
  // 接点 P1: v から prev 方向へ tangentLen（つまり -e1 を正規化して v に加算）。
  const p1x = v[0] - (e1x / len1) * tangentLen;
  const p1y = v[1] - (e1y / len1) * tangentLen;
  // 接点 P2: v から next 方向へ tangentLen（+e2 を正規化）。
  const p2x = v[0] + (e2x / len2) * tangentLen;
  const p2y = v[1] + (e2y / len2) * tangentLen;

  // 円弧中心 C = v + bisector_unit * (rEff / sinHalf)。
  // bisector は -e1/|e1| + e2/|e2| を正規化（v から内側を指す）。
  const bx = -e1x / len1 + e2x / len2;
  const by = -e1y / len1 + e2y / len2;
  const bLen = Math.hypot(bx, by);
  if (bLen < 1e-9) return [v]; // 退化フォールバック（内角 ≒ π の保険）。
  const cDist = rEff / sinHalf;
  const cx = v[0] + (bx / bLen) * cDist;
  const cy = v[1] + (by / bLen) * cDist;

  // C → P1, C → P2 を slerp 等分。外角 = π - interior = 2 * (π/2 - halfInt)。
  const arcAngle = Math.PI - 2 * halfInt;
  const segs = Math.max(4, Math.min(8, Math.round(arcAngle / (Math.PI / 8))));
  const v1x = p1x - cx;
  const v1y = p1y - cy;
  const v2x = p2x - cx;
  const v2y = p2y - cy;

  // 回転方向は v1 → v2 の cross 符号で決まる。slerp は各 t で角度補間。
  const cross = v1x * v2y - v1y * v2x;
  const dot = v1x * v2x + v1y * v2y;
  const sweep = Math.atan2(cross, dot); // [-π, π]、回転方向込みの掃引角。

  const out: Vec2[] = [];
  for (let s = 0; s <= segs; s++) {
    const t = s / segs;
    const a = sweep * t;
    const cosA = Math.cos(a);
    const sinA = Math.sin(a);
    out.push([cx + v1x * cosA - v1y * sinA, cy + v1x * sinA + v1y * cosA]);
  }
  return out;
}

// ────────────────────────────────────────────────────────────────────────────
// buildSilhouette — v1.1 Phase D Step 2 追加
// ────────────────────────────────────────────────────────────────────────────

/**
 * subdivide の `perEdge` を元頂点数 `N` から導出する。
 *
 * 式は renderer.ts（Phase C まで inline だったもの）の移植:
 * `perEdge = max(6, floor(72 / max(1, N)))`。72 / N は「polygon 全体で総
 * 頂点数がほぼ 72 になる」よう調整した感覚値で、N=6 なら 12、N=12 なら 6、
 * N=24 なら 6（下限）と頂点数に反比例する。下限 6 はテキスト追従の
 * 最低サンプル密度を保証する（短辺でも tangent 推定が破綻しない）。
 *
 * private helper（buildSilhouette 専用）。外部公開は不要（呼び出し側は
 * `buildSilhouette` 経由で perEdge を意識せずに使える設計）。
 */
function derivePerEdge(N: number): number {
  return Math.max(6, Math.floor(72 / Math.max(1, N)));
}

/**
 * AppState + Scene から silhouette を構築する統合関数（Phase D Step 2 追加）。
 *
 * 従来 `renderer.ts` に inline されていた `subdivide → warp` を core 層へ引き
 * 上げ、さらに Phase D の `applyBreath` を後段で合成する。呼び出し側
 * （renderer.ts, export/webmExport.ts 等）は本関数 1 発で silhouette を得られる。
 *
 * 手順:
 * 1. `subdividePolygon(scene.innerPoly, perEdge)` で細分化
 * 2. `scene.cells` から `isPhantom=false` の real cell のみ抽出し
 *    `warpPolygonByCells(subdivided, realCells, state.progress)` で warp
 * 3. `t` が渡され、**かつ** `state.progress === 0` のとき
 *    `applyBreath(warped, t, state.breath)` で呼吸を適用
 *
 * **Breath 適用条件**（DETONATE 中は凍結）:
 * - `progress > 0`（破壊中）は Breath 非適用。爆破アニメの厳密な時相を
 *   呼吸が撹乱しないようにする（spec §3.3 の二相モデル整合）。
 * - `t === undefined` は Phase C までの後方互換パスで、呼吸なし。
 *
 * **scene 引数を外部受け取りにする理由**:
 * 元仕様書では `buildSilhouette(state, t?)` だが、現状 `renderer.ts` が
 * `state.sceneCache ?? buildScene(L, state)` のフォールバックロジックを
 * 持っており、これを本関数へ移すと scene 取得の責務が多層化する。関心を
 * 分離するため、scene 取得は呼び出し側の責務として残す（本関数は純粋な
 * 幾何変換のパイプライン）。
 *
 * pure 性: Phase H Step 1 で Crackle/Rotation を削除し `applyBreath` は
 * noise を使わなくなったため、seed 依存も消えた。同 `t` + 同 `params` +
 * 同 silhouette で決定論的に同じ結果を返す。
 *
 * @param state 状態スナップショット（`progress`, `breath` を参照）。
 * @param scene `Scene`（`innerPoly`, `cells`）。呼び出し側で sceneCache
 *              フォールバックを解決してから渡す。
 * @param t 経過秒（`Clock.t` 由来）。未指定なら Breath を適用しない
 *          （Phase C 互換）。
 * @returns silhouette 頂点列。空 innerPoly では空配列。
 */
export function buildSilhouette(state: AppState, scene: Scene, t?: number): Vec2[] {
  const N = scene.innerPoly.length;
  if (N === 0) return [];

  const perEdge = derivePerEdge(N);
  const subdivided = subdividePolygon(scene.innerPoly, perEdge);
  // phantom cell は仮想変位（境界補間用）しか持たないため silhouette に
  // 反映させない（renderer.ts 旧実装と同じ filter 規約）。
  const realCells = scene.cells.filter((c) => !c.isPhantom);
  const warped = warpPolygonByCells(subdivided, realCells, state.progress);

  // Breath は progress=0 の idle 時のみ適用。t 未指定時は後方互換で skip。
  if (t !== undefined && state.progress === 0) {
    return applyBreath(warped, t, state.breath);
  }
  return warped;
}

/**
 * silhouette path の鋭角頂点を小円弧で丸めた path を返す。
 * 元の path は変更しない（pure function）。
 *
 * v1.1 Phase C.1 Step 1 で追加。**wireframe 対応箇所はない** — 既存
 * `buildTextPath` は線形補間のみで角を保つため、丸めた path を渡すことで
 * テキスト経路の角の張り出しを抑える独立フィルタとして機能する。
 *
 * アルゴリズム（方式 A: 接点 + 円弧補間）:
 * 1. polygon の signed area 符号で巻き向き（CCW/CW）を 1 度だけ判定。
 * 2. 各頂点を「入力ベースの prev / next」で独立に内角計算。
 *    - 連続する鋭角でも補間後の頂点を参照しないため、結果が
 *      頂点順序や処理順に依存しない（idempotent な per-vertex 処理）。
 * 3. 内角 < threshold の頂点に対し:
 *    - 半径を `min(roundRadius, 0.4 × min(|e1|,|e2|), 接線長基準逆算)` で clamp。
 *    - 接点 P1 / P2（v から両辺方向へ接線長 `r/tan(interior/2)` 戻った位置）と、
 *      円弧中心 C（v から二等分線方向へ `r/sin(interior/2)` 進んだ位置）を計算。
 *    - C 起点の slerp で `segs = clamp(round((π-interior)/(π/8)), 4, 8)` 個補間。
 * 4. 内角 ≥ threshold、または退化辺の頂点はそのまま push。
 *
 * 設計意図:
 * - 既存 `buildTextPath` は `lineTo` 相当の線形サンプリングのため、頂点数が
 *   増えても自然に経路が滑らかになる（円弧専用 API は不要）。
 * - roundRadius は外部で fontSize 連動を計算して渡す契約（本関数は受け取った
 *   絶対値のみで動作）。これにより本関数を layout に依存させない。
 * - 戻り値は `Vec2[]` 単独。呼び出し側は `cornerIdx=[]` で `buildTextPath` に
 *   流すため、index 再マップ（`indexMap`）は不要。
 *
 * エッジケース:
 * - `path.length < 3` → 入力をシャローコピーして返す（pure 性維持）。
 * - 退化辺（`|e| < 1e-6`） → その頂点はスキップして原値保持。
 * - 内角 ≥ threshold → 原値保持。
 * - 直線近傍（sin/tan(interior/2) ≒ 0） → 原値保持。
 * - signed area ≒ 0（退化 polygon）→ CCW として処理（実害なし）。
 *
 * @param path 元のパス（閉じた多角形、末尾頂点が先頭と一致しない前提）。
 * @param angleThresholdDeg この角度（degree、内角）未満の頂点を丸める。
 * @param roundRadius 丸め半径（px、外部で fontSize 連動を計算して渡す）。
 * @returns 丸め処理後のパス（頂点数が増える）。元 path は変更されない。
 */
export function roundSharpCorners(
  path: Vec2[],
  angleThresholdDeg: number,
  roundRadius: number,
): Vec2[] {
  const N = path.length;
  if (N < 3) return [...path];

  const ccw = signedArea(path) > 0;
  const out: Vec2[] = [];
  for (let i = 0; i < N; i++) {
    const prev = path[(i - 1 + N) % N];
    const v = path[i];
    const next = path[(i + 1) % N];
    if (!prev || !v || !next) continue;
    const arc = roundOneCorner(prev, v, next, ccw, angleThresholdDeg, roundRadius);
    if (arc === null) out.push(v);
    else out.push(...arc);
  }
  return out;
}
